import {
  PublicClientApplication,
  AuthenticationResult,
  InteractionRequiredAuthError,
  ProtocolMode,
  LogLevel,
} from '@azure/msal-browser';
import { localStorageService } from './localstorage.service';
import { observationDraftService } from './observationDraftService';
import { logger } from '../utils/logger';
import {
  buildInteractiveLoginRequest,
  buildSilentRequestFromLoginRequest,
  shouldPromptForInteraction,
  createMobileLoginRequest,
  createSilentTokenRequest,
} from './auth/loginRequestBuilder';
import {
  initializeMsalErrorHandling,
  handleMsalError,
  clearMsalErrors,
  clearStaleMsalCache,
} from './auth/msalErrorHandler';

// Check required environment variables
// [existing environment variable checks]

// Validate redirect URI configuration
const validateRedirectUri = (): string => {
  const envRedirectUri = import.meta.env.VITE_AZURE_REDIRECT_URI;
  const currentOrigin = window.location.origin;

  if (envRedirectUri) {
    // Ensure no trailing slash mismatch
    const normalizedEnvUri = envRedirectUri.replace(/\/$/, '');
    const normalizedOrigin = currentOrigin.replace(/\/$/, '');

    if (normalizedEnvUri !== normalizedOrigin) {
      logger.warn(
        `Redirect URI mismatch: env=${normalizedEnvUri}, origin=${normalizedOrigin}`,
      );
      logger.info('Using environment variable VITE_AZURE_REDIRECT_URI');
    }
    return normalizedEnvUri;
  }

  logger.info('No VITE_AZURE_REDIRECT_URI set, using window.location.origin');
  return currentOrigin;
};

// Azure AD configuration from environment variables
const msalConfig = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID,
    authority: import.meta.env.VITE_AZURE_AUTHORITY,
    redirectUri: validateRedirectUri(),
    postLogoutRedirectUri: window.location.origin,
    protocolMode: ProtocolMode.OIDC,
    navigateToLoginRequestUrl: true, // Allow navigation for redirect flow
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: true,
  },
  system: {
    // Setting the token renewal offset to refresh token 5 minutes before expiration
    tokenRenewalOffsetSeconds: 300,
    loggerOptions: {
      // [existing logger options]
      loggerCallback: (
        level: LogLevel,
        message: string,
        containsPii: boolean,
      ) => {
        if (containsPii) return;
        if (level >= 3) {
          console.error(message);
        } else if (level === 2) {
          console.warn(message);
        } else if (level === 1) {
          console.info(message);
        } else {
          console.debug(message);
        }
      },
      logLevel: parseInt(import.meta.env.VITE_AZURE_AUTH_DEBUG_LEVEL || '2'),
    },
  },
};

// MSAL instance singleton
let msalInstanceSingleton: PublicClientApplication | null = null;
let isInitializing = false;
let initializationPromise: Promise<void> | null = null;

// Get or create MSAL instance
const getMsalInstance = (): PublicClientApplication => {
  if (!msalInstanceSingleton) {
    logger.info('Creating MSAL instance...');
    msalInstanceSingleton = new PublicClientApplication(msalConfig);
  }
  return msalInstanceSingleton;
};

// Export for backward compatibility
export const msalInstance = getMsalInstance();

// Login request parameters using the builder pattern
const loginRequest = createMobileLoginRequest();

// Token refresh timer reference
let tokenRefreshTimer: number | null = null;

// Helper function to parse JWT and get expiration time
const getTokenExpirationTime = (token: string): number | null => {
  try {
    const tokenParts = token.split('.');
    if (tokenParts.length !== 3) {
      logger.warn(
        'Invalid token format - expected 3 parts, got ' + tokenParts.length,
      );
      return null;
    }

    const payload = JSON.parse(atob(tokenParts[1]));

    // Debug log the payload structure
    logger.debug('Token payload claims: ' + Object.keys(payload).join(', '));

    if (!payload.exp) {
      logger.warn(
        'Token does not contain expiration claim. Available claims: ' +
          Object.keys(payload).join(', '),
      );
      // Check for alternative expiration fields
      if (payload.expires_on) {
        return payload.expires_on * 1000;
      }
      if (payload.expiresOn) {
        return payload.expiresOn * 1000;
      }
      return null;
    }

    return payload.exp * 1000; // Convert to milliseconds
  } catch (error) {
    logger.error(
      'Error parsing token: ' +
        (error instanceof Error ? error.message : String(error)),
    );
    return null;
  }
};

// acquireTokenRedirect navigates away, and the visibilitychange/pagehide flush in
// observationDraftService is not awaited, so field edits still inside the draft
// debounce would be lost. An edit typed while a flush is writing is still dirty
// when that flush resolves, so flush again until nothing is pending. A failed
// flush, or edits that never settle, are logged but must not block sign-in.
const MAX_DRAFT_FLUSHES_BEFORE_REDIRECT = 3;

const flushDraftsBeforeRedirect = async (): Promise<void> => {
  try {
    for (let pass = 0; pass < MAX_DRAFT_FLUSHES_BEFORE_REDIRECT; pass++) {
      await observationDraftService.flushAll();
      if (!observationDraftService.hasPendingChanges()) {
        return;
      }
    }
    logger.error(
      `Field drafts still unsaved after ${MAX_DRAFT_FLUSHES_BEFORE_REDIRECT} saves before token redirect`,
    );
  } catch (error) {
    logger.error(
      'Failed to save field drafts before token redirect: ' +
        (error instanceof Error ? error.message : String(error)),
    );
  }
};

// Wrap MSAL login with our own implementation
const azureAuthService = {
  // Process redirect response and store token
  processRedirectResponse: async (response: AuthenticationResult) => {
    try {
      const instance = getMsalInstance();

      // Ensure we have the latest token
      const account = instance.getAllAccounts()[0];
      if (!account) {
        logger.error('No account found after redirect');
        return null;
      }

      const silentRequest = createSilentTokenRequest(account);
      const tokenResponse = await instance.acquireTokenSilent(silentRequest);

      if (tokenResponse && tokenResponse.accessToken) {
        localStorage.setItem('jwtToken', tokenResponse.accessToken);

        // Validate token storage with retry mechanism
        let retryCount = 0;
        let tokenPersisted = false;

        while (retryCount < 3 && !tokenPersisted) {
          await new Promise((resolve) =>
            setTimeout(resolve, 50 + retryCount * 50),
          );
          const storedToken = localStorage.getItem('jwtToken');

          if (storedToken === tokenResponse.accessToken) {
            tokenPersisted = true;
            logger.info(
              'Access token stored from redirect response: ' +
                storedToken.substring(0, 10) +
                '...',
            );
          } else {
            retryCount++;
            logger.warn(
              `Token storage attempt ${retryCount} failed, retrying...`,
            );
            localStorage.setItem('jwtToken', tokenResponse.accessToken);
          }
        }

        if (!tokenPersisted) {
          logger.error('Failed to persist token after 3 attempts');
          return response;
        }

        // Set up token refresh schedule
        azureAuthService.scheduleTokenRefresh(tokenResponse.accessToken);
      }

      return response;
    } catch (error) {
      logger.error(
        'Error processing redirect response: ' +
          (error instanceof Error ? error.message : String(error)),
      );
      return null;
    }
  },

  // Initialize MSAL instance and handle redirect flow
  initialize: async () => {
    const instance = getMsalInstance();

    // Initialize error handling
    initializeMsalErrorHandling();

    // Prevent duplicate initialization
    if (isInitializing && initializationPromise) {
      logger.debug('MSAL initialization already in progress, waiting...');
      await initializationPromise;

      // After waiting, try to handle redirect
      try {
        const response = await instance.handleRedirectPromise();
        if (response) {
          return await azureAuthService.processRedirectResponse(response);
        }
      } catch (error) {
        logger.error(
          'Error handling redirect after waiting for init: ' +
            (error instanceof Error ? error.message : String(error)),
        );
      }
      return null;
    }

    // Check if already initialized
    let needsInit = false;
    try {
      const accounts = instance.getAllAccounts();
      // If we can get accounts, it's initialized
      logger.debug(
        'MSAL instance already initialized with ' +
          accounts.length +
          ' accounts',
      );
    } catch (e) {
      needsInit = true;
    }

    if (needsInit) {
      // Not initialized yet, initialize now
      logger.info('Initializing MSAL instance...');

      // Clear any stale cache before initialization
      clearStaleMsalCache();

      isInitializing = true;
      initializationPromise = instance
        .initialize()
        .then(() => {
          isInitializing = false;
          initializationPromise = null;
          logger.info('MSAL initialization successful');
        })
        .catch((error) => {
          isInitializing = false;
          initializationPromise = null;
          logger.error(
            'MSAL initialization failed: ' +
              (error instanceof Error ? error.message : String(error)),
          );
          handleMsalError(error);
          throw error;
        });
      await initializationPromise;
    }

    logger.info('Handling redirect promise...');
    try {
      // This returns the response if this page load is the result of a redirect
      const response = await instance.handleRedirectPromise().catch((error) => {
        logger.error(
          'Error handling redirect promise: ' +
            (error instanceof Error ? error.message : String(error)),
        );
        if (handleMsalError(error)) {
          // Error was handled, try once more
          return instance.handleRedirectPromise();
        }
        throw error;
      });

      if (response) {
        logger.info('Redirect response received');
        return await azureAuthService.processRedirectResponse(response);
      } else {
        logger.info('No redirect response');

        // Check if we have a token and set up refresh if needed
        const existingToken = localStorage.getItem('jwtToken');
        if (existingToken) {
          azureAuthService.scheduleTokenRefresh(existingToken);
        }
      }
    } catch (error) {
      logger.error(
        'Error handling redirect: ' +
          (error instanceof Error ? error.message : String(error)),
      );
    }

    return null; // No redirect was handled
  },

  // Schedule token refresh based on token expiration
  scheduleTokenRefresh: (token: string) => {
    // Clear any existing timer
    if (tokenRefreshTimer !== null) {
      window.clearTimeout(tokenRefreshTimer);
      tokenRefreshTimer = null;
    }

    const expirationTime = getTokenExpirationTime(token);
    if (!expirationTime) {
      logger.warn('Could not determine token expiration time');
      // Fallback to refreshing every 10 minutes as a safety measure
      tokenRefreshTimer = window.setTimeout(
        () => {
          azureAuthService.refreshTokenIfNeeded();
        },
        10 * 60 * 1000,
      );
      return;
    }

    const now = Date.now();
    // Calculate time to refresh (5 minutes before expiration)
    const timeToRefresh = expirationTime - now - 5 * 60 * 1000;

    logger.info(
      `Token expires in ${Math.round((expirationTime - now) / 60000)} minutes. Will refresh in ${Math.round(timeToRefresh / 60000)} minutes.`,
    );

    // Set timer to refresh token
    if (timeToRefresh > 0) {
      tokenRefreshTimer = window.setTimeout(() => {
        azureAuthService.refreshTokenIfNeeded();
      }, timeToRefresh);
    } else {
      // Token is already close to expiration, refresh now
      logger.info('Token is close to expiration, refreshing now');
      azureAuthService.refreshTokenIfNeeded();
    }
  },

  // Check if token needs refresh and refresh it
  refreshTokenIfNeeded: async () => {
    logger.info('Checking if token needs refresh...');

    const token = localStorage.getItem('jwtToken');
    if (!token) {
      logger.info('No token found to refresh');
      return;
    }

    try {
      const expirationTime = getTokenExpirationTime(token);
      const now = Date.now();

      if (!expirationTime || now >= expirationTime - 5 * 60 * 1000) {
        logger.info('Token needs refresh, acquiring new token silently');

        const accounts = getMsalInstance().getAllAccounts();
        if (accounts.length === 0) {
          logger.warn('No accounts found when attempting to refresh token');
          return;
        }

        const tokenRequest = createSilentTokenRequest(accounts[0]);
        tokenRequest.forceRefresh = true;

        const response =
          await getMsalInstance().acquireTokenSilent(tokenRequest);

        if (response && response.accessToken) {
          localStorage.setItem('jwtToken', response.accessToken);
          logger.info('Token refreshed successfully');

          // Schedule next refresh
          azureAuthService.scheduleTokenRefresh(response.accessToken);
        }
      } else {
        logger.info('Token is still valid, no refresh needed');
        // Reschedule refresh
        azureAuthService.scheduleTokenRefresh(token);
      }
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        logger.warn('Interactive authentication required for token refresh');
        // We'll handle this in the background without interrupting the user
        // by trying acquireTokenRedirect with no prompt first
        try {
          const redirectRequest = {
            ...loginRequest,
            prompt: 'none', // Try with no prompt first
          };

          await flushDraftsBeforeRedirect();
          await getMsalInstance().acquireTokenRedirect(redirectRequest);
        } catch (redirectError) {
          logger.error(
            'Error refreshing token with redirect: ' +
              (redirectError instanceof Error
                ? redirectError.message
                : String(redirectError)),
          );

          // Add a background check in case user comes back to app
          // after token expires completely
          tokenRefreshTimer = window.setTimeout(
            () => {
              azureAuthService.refreshTokenIfNeeded();
            },
            5 * 60 * 1000,
          ); // Check again in 5 minutes
        }
      } else {
        logger.error(
          'Error refreshing token: ' +
            (error instanceof Error ? error.message : String(error)),
        );

        // Schedule another attempt
        tokenRefreshTimer = window.setTimeout(
          () => {
            azureAuthService.refreshTokenIfNeeded();
          },
          5 * 60 * 1000,
        ); // Try again in 5 minutes
      }
    }
  },

  // Renews the token even when it looks valid locally, for when the API has refused it
  // (revoked, or its claims changed). Never redirects: a renewal that needs the user
  // rejects, and the request that was refused keeps its 401.
  renewTokenSilently: async (): Promise<void> => {
    const [account] = getMsalInstance().getAllAccounts();
    if (!account) {
      throw new Error('No signed-in account to renew the token for');
    }
    const tokenRequest = createSilentTokenRequest(account);
    tokenRequest.forceRefresh = true;
    const response = await getMsalInstance().acquireTokenSilent(tokenRequest);
    localStorage.setItem('jwtToken', response.accessToken);
    azureAuthService.scheduleTokenRefresh(response.accessToken);
  },

  // Check if user is logged in
  isAuthenticated: (): boolean => {
    const accounts = getMsalInstance().getAllAccounts();
    const hasToken = !!localStorage.getItem('jwtToken');
    return accounts.length > 0 && hasToken;
  },

  // Get current user account
  getCurrentUser: () => {
    const accounts = getMsalInstance().getAllAccounts();
    if (accounts.length === 0) return null;
    return accounts[0];
  },

  // Login using redirect with improved token handling
  loginRedirect: async (request = loginRequest): Promise<void> => {
    try {
      // Build standardized login request
      const enhancedRequest = buildInteractiveLoginRequest(request);
      logger.info(
        'Attempting to login with scopes: ' + enhancedRequest.scopes?.join(' '),
      );

      localStorageService.clearMsalItems();

      // Clear any error states before login
      clearMsalErrors();

      // Perform the login with redirect
      await getMsalInstance().loginRedirect(enhancedRequest);

      // Note: This method will cause a page redirect, so code after this won't execute
      logger.info('Login redirect initiated');
    } catch (error) {
      logger.error(
        'Login redirect error: ' +
          (error instanceof Error ? error.message : String(error)),
      );
      handleMsalError(error);
      throw error;
    }
  },

  // Acquire token silently with enhanced error handling and logging
  acquireToken: async (
    request = loginRequest,
  ): Promise<AuthenticationResult | null> => {
    try {
      const accounts = getMsalInstance().getAllAccounts();
      if (accounts.length === 0) {
        logger.warn('No accounts found when attempting to acquire token');
        return null;
      }

      logger.info(
        'Attempting to acquire token silently for account: ' +
          accounts[0].username,
      );

      // Build silent request from login request
      const tokenRequest = buildSilentRequestFromLoginRequest(request);
      tokenRequest.account = accounts[0];
      tokenRequest.forceRefresh = true;

      const response = await getMsalInstance().acquireTokenSilent(tokenRequest);

      // Log successful token acquisition (without exposing token contents)
      logger.info(
        'Token acquired successfully with scopes: ' + response.scopes.join(' '),
      );

      // Store token in localStorage for backward compatibility with existing API code
      if (response && response.accessToken) {
        localStorage.setItem('jwtToken', response.accessToken);
        // Force the localStorage update to be committed
        const storedToken = localStorage.getItem('jwtToken');
        logger.info(
          'Token stored in localStorage: ' + (storedToken ? 'YES' : 'NO'),
        );
        // Ensure localStorage has time to update
        await new Promise((resolve) => setTimeout(resolve, 50));

        // Set up token refresh schedule
        azureAuthService.scheduleTokenRefresh(response.accessToken);
      }

      return response;
    } catch (error) {
      // Check if error requires interaction
      if (shouldPromptForInteraction(error)) {
        logger.info('Interactive authentication required, using redirect...');
        try {
          const redirectRequest = buildInteractiveLoginRequest(loginRequest);

          await flushDraftsBeforeRedirect();
          localStorageService.clearMsalItems();

          await getMsalInstance().acquireTokenRedirect(redirectRequest);
          // Note: redirect will cause page reload, so we won't reach this point
          return null;
        } catch (redirectError) {
          logger.error(
            'Error acquiring token with redirect: ' +
              (redirectError instanceof Error
                ? redirectError.message
                : String(redirectError)),
          );
          handleMsalError(redirectError);
          return null;
        }
      }
      logger.error(
        'Error acquiring token silently: ' +
          (error instanceof Error ? error.message : String(error)),
      );
      return null;
    }
  },

  // Logout from the application only (not from Microsoft)
  logout: async () => {
    try {
      logger.info('Logging out user from application...');

      // Clear token refresh timer
      if (tokenRefreshTimer !== null) {
        window.clearTimeout(tokenRefreshTimer);
        tokenRefreshTimer = null;
      }

      // Store rememberedUser value before logout if it exists
      const rememberedUser = localStorage.getItem('rememberedUser');

      // IMPORTANT: Clear MSAL accounts to properly logout
      const msalInstance = getMsalInstance();
      const accounts = msalInstance.getAllAccounts();

      if (accounts.length > 0) {
        logger.info(
          `Clearing ${accounts.length} MSAL account(s) from cache...`,
        );

        // Set active account to null
        msalInstance.setActiveAccount(null);

        // Clear MSAL cache completely
        // This is the most reliable way to clear authentication state
        try {
          await msalInstance.clearCache();
          logger.info('Cleared MSAL cache successfully');
        } catch (e) {
          // Fallback: manually clear all MSAL storage items
          logger.warn(
            'clearCache failed, manually clearing MSAL storage: ' +
              (e instanceof Error ? e.message : String(e)),
          );

          // Clear all MSAL-related items from localStorage
          const keysToRemove: string[] = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (key.includes('msal') || key.includes('login-hint'))) {
              keysToRemove.push(key);
            }
          }

          keysToRemove.forEach((key) => {
            localStorage.removeItem(key);
            logger.debug(`Removed localStorage key: ${key}`);
          });

          // Also clear sessionStorage
          for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && (key.includes('msal') || key.includes('login-hint'))) {
              sessionStorage.removeItem(key);
              logger.debug(`Removed sessionStorage key: ${key}`);
            }
          }
        }

        logger.info('Cleared all MSAL accounts');
      }

      // Clear all MSAL localStorage items
      localStorageService.clearMsalItems();

      // Restore rememberedUser if it existed
      if (rememberedUser) {
        localStorage.setItem('rememberedUser', rememberedUser);
        logger.info('Preserved remembered username during logout');
      }

      logger.info('Cleared MSAL cache and localStorage');

      // Clear IndexedDB databases
      const databases = await indexedDB.databases();
      databases.forEach(async (db) => {
        db.name && indexedDB.deleteDatabase(db.name);
      });

      // Clear sync date
      localStorage.removeItem('lastSyncDate');
      localStorage.removeItem('lastSyncResult');

      // Try to clear any additional MSAL browser storage
      try {
        // Clear any cached MSAL data in sessionStorage
        Object.keys(sessionStorage).forEach((key) => {
          if (key.includes('msal')) {
            sessionStorage.removeItem(key);
          }
        });
        logger.debug('Cleared MSAL sessionStorage items');
      } catch (e) {
        logger.debug(
          'Could not clear sessionStorage: ' +
            (e instanceof Error ? e.message : String(e)),
        );
      }

      logger.info(
        'User logged out from application successfully - MSAL state cleared',
      );
    } catch (error) {
      logger.error(
        'Logout error: ' +
          (error instanceof Error ? error.message : String(error)),
      );
    }
  },

  // Full logout (including Microsoft session)
  logoutCompletely: async () => {
    try {
      logger.info('Logging out user completely...');

      // Clear token refresh timer
      if (tokenRefreshTimer !== null) {
        window.clearTimeout(tokenRefreshTimer);
        tokenRefreshTimer = null;
      }

      // Store rememberedUser value before logout if it exists
      const rememberedUser = localStorage.getItem('rememberedUser');

      // Clear local storage token
      localStorage.removeItem('jwtToken');

      // Restore rememberedUser if it existed
      if (rememberedUser) {
        localStorage.setItem('rememberedUser', rememberedUser);
        logger.info('Preserved remembered username during complete logout');
      }

      try {
        // Logout from MSAL (full logout including Microsoft)
        // This will clear the MSAL cache and end the Microsoft session
        await getMsalInstance().logoutRedirect();
        logger.info('Successfully triggered MSAL logout redirect');
      } catch (msalError) {
        // If MSAL logout fails, log the error
        logger.error(
          'MSAL logout redirect failed: ' +
            (msalError instanceof Error
              ? msalError.message
              : String(msalError)),
        );
      }

      logger.info('User logged out completely');
    } catch (error) {
      logger.error(
        'Complete logout error: ' +
          (error instanceof Error ? error.message : String(error)),
      );
    }
  },

  // Get token claim
  getTokenClaim: (claim: string): string | null => {
    try {
      const accounts = getMsalInstance().getAllAccounts();
      if (accounts.length === 0) {
        logger.warn('No accounts found when attempting to get token claim');
        return null;
      }

      const idTokenClaims = accounts[0].idTokenClaims as Record<string, any>;
      logger.debug('Retrieved token claims for ' + claim);
      return idTokenClaims[claim] || null;
    } catch (error) {
      logger.error(
        'Error getting token claim: ' +
          (error instanceof Error ? error.message : String(error)),
      );
      return null;
    }
  },

  // Check token expiration and initiate refresh if token is nearing expiration
  isTokenExpired: (): boolean => {
    try {
      const token = localStorage.getItem('jwtToken');
      if (!token) {
        return true;
      }

      const expirationTime = getTokenExpirationTime(token);
      if (!expirationTime) {
        return true;
      }

      // Consider token expired if it's less than 5 minutes from expiration
      const now = Date.now();
      return now >= expirationTime - 5 * 60 * 1000;
    } catch (error) {
      logger.error(
        'Error checking token expiration: ' +
          (error instanceof Error ? error.message : String(error)),
      );
      return true; // Assume expired on error
    }
  },

  // Validate local token against MSAL state and refresh if needed
  validateAndRefreshToken: async (): Promise<boolean> => {
    try {
      logger.info('Validating and refreshing token...');

      // Ensure MSAL is initialized before any operations
      const instance = getMsalInstance();
      try {
        // Try to get accounts to check if initialized
        instance.getAllAccounts();
      } catch (e) {
        // Not initialized, initialize it now
        logger.info('MSAL not initialized, initializing now...');
        await instance.initialize();
      }

      // Check for MSAL accounts first (most reliable indicator of login state)
      const accounts = instance.getAllAccounts();
      logger.info(`Found ${accounts.length} MSAL accounts`);

      if (accounts.length > 0) {
        // Log the current token info for debugging
        const currentToken = localStorage.getItem('jwtToken');
        if (currentToken) {
          logger.debug(
            'Current token first 50 chars: ' + currentToken.substring(0, 50),
          );
        }

        // Check if current token is near expiration
        if (azureAuthService.isTokenExpired()) {
          logger.info(
            'Token is expired or nearing expiration, acquiring fresh token...',
          );

          try {
            // Attempt to acquire token silently to refresh
            const tokenResponse = await azureAuthService.acquireToken();

            if (tokenResponse && tokenResponse.accessToken) {
              // Update the token in localStorage - using ACCESS token, not ID token
              localStorage.setItem('jwtToken', tokenResponse.accessToken);
              logger.info('Token refreshed successfully');

              // Set up token refresh schedule
              azureAuthService.scheduleTokenRefresh(tokenResponse.accessToken);

              return true;
            } else {
              logger.warn(
                'Failed to acquire token despite having MSAL account',
              );
            }
          } catch (tokenError) {
            logger.error(
              'Error acquiring token: ' +
                (tokenError instanceof Error
                  ? tokenError.message
                  : String(tokenError)),
            );
          }
        } else {
          logger.info('Token is still valid');
          return true;
        }
      }

      // If we couldn't get a token through MSAL account, check localStorage
      const localToken = localStorage.getItem('jwtToken');

      // If no local token, not authenticated
      if (!localToken) {
        logger.info('No token found in localStorage');
        return false;
      }

      // If we have a local token but no MSAL accounts, the token might be invalid
      if (accounts.length === 0) {
        logger.info('No MSAL accounts found despite having localStorage token');
        logger.info('Clearing potentially invalid token');
        localStorage.removeItem('jwtToken');

        // Clear token refresh timer
        if (tokenRefreshTimer !== null) {
          window.clearTimeout(tokenRefreshTimer);
          tokenRefreshTimer = null;
        }

        return false;
      }

      // If we get here, we have a token but failed to refresh it
      logger.info('Using existing token from localStorage');

      // Make sure we have a refresh timer set up
      if (tokenRefreshTimer === null) {
        azureAuthService.scheduleTokenRefresh(localToken);
      }

      // We'll consider the existing token valid for now
      return true;
    } catch (error) {
      logger.error(
        'Token validation error: ' +
          (error instanceof Error ? error.message : String(error)),
      );
      return false;
    }
  },

  // Get current access token
  getAccessToken: (): string | null => {
    return localStorage.getItem('jwtToken');
  },

  // Helper methods for "Remember Me" functionality
  rememberMe: {
    // [existing rememberMe implementation]
    saveUsername: (username: string) => {
      try {
        if (username && username.trim()) {
          localStorage.setItem('rememberedUser', username.trim());
          logger.info('Username saved for "Remember Me"');
          return true;
        }
        return false;
      } catch (error) {
        logger.error(
          'Error saving remembered username: ' +
            (error instanceof Error ? error.message : String(error)),
        );
        return false;
      }
    },

    getUsername: (): string | null => {
      try {
        return localStorage.getItem('rememberedUser');
      } catch (error) {
        logger.error(
          'Error getting remembered username: ' +
            (error instanceof Error ? error.message : String(error)),
        );
        return null;
      }
    },

    clearUsername: () => {
      try {
        localStorage.removeItem('rememberedUser');
        logger.info('Remembered username cleared');
        return true;
      } catch (error) {
        logger.error(
          'Error clearing remembered username: ' +
            (error instanceof Error ? error.message : String(error)),
        );
        return false;
      }
    },

    hasRememberedUser: (): boolean => {
      return localStorage.getItem('rememberedUser') !== null;
    },
  },
};

// Add a window event listener to detect when the app comes back into focus
// This helps refresh tokens if the user has been away for a while
let lastFocusCheck = Date.now();
const MIN_TIME_BETWEEN_CHECKS = 60 * 1000; // Only check every 60 seconds minimum

if (typeof window !== 'undefined') {
  window.addEventListener('focus', () => {
    const now = Date.now();
    const timeSinceLastCheck = now - lastFocusCheck;

    // Only validate if it's been at least 60 seconds since last check
    if (timeSinceLastCheck >= MIN_TIME_BETWEEN_CHECKS) {
      logger.info(
        'Window focus detected after being away, validating token...',
      );
      lastFocusCheck = now;
      // Check if token is still valid, refresh if needed
      azureAuthService.validateAndRefreshToken();
    }
  });

  // Also set up periodic check in case token refresh timer fails
  setInterval(
    () => {
      if (azureAuthService.isAuthenticated()) {
        if (azureAuthService.isTokenExpired()) {
          logger.info('Periodic check found expired token, refreshing...');
          azureAuthService.refreshTokenIfNeeded();
        }
      }
    },
    10 * 60 * 1000,
  ); // Check every 10 minutes
}

export default azureAuthService;
